import React, { useCallback, useEffect, useRef } from 'react';
import type { Block as BlockType } from '../types';
interface BlockProps {
  block: BlockType;
  index: number;
  isFocused: boolean;
  onUpdate: (content: string) => void;
  onEnter: () => void;
  onDelete: () => void;
  onFocus: () => void;
  onSlashCommand: (filter: string) => void;
  onCloseSlash: () => void;
  registerRef: (id: string, el: HTMLElement | null) => void;
}
export function BlockItem({
  block,
  index,
  isFocused,
  onUpdate,
  onEnter,
  onDelete,
  onFocus,
  onSlashCommand,
  onCloseSlash,
  registerRef
}: BlockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isComposing = useRef(false);
  useEffect(() => {
    registerRef(block.id, ref.current);
  }, [block.id, registerRef]);
  useEffect(() => {
    if (isFocused && ref.current && document.activeElement !== ref.current) {
      ref.current.focus();
      // Place cursor at end
      const range = document.createRange();
      const sel = window.getSelection();
      if (ref.current.childNodes.length > 0) {
        range.selectNodeContents(ref.current);
        range.collapse(false);
      } else {
        range.setStart(ref.current, 0);
        range.collapse(true);
      }
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isFocused]);
  const handleInput = useCallback(() => {
    if (!ref.current) return;
    const text = ref.current.textContent ?? '';
    onUpdate(text);
    if (text.startsWith('/')) {
      onSlashCommand(text.slice(1));
    } else {
      onCloseSlash();
    }
  }, [onUpdate, onSlashCommand, onCloseSlash]);
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (isComposing.current) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onCloseSlash();
      onEnter();
    }
    if (e.key === 'Backspace') {
      const text = ref.current?.textContent ?? '';
      if (text === '') {
        e.preventDefault();
        onCloseSlash();
        onDelete();
      }
    }
    if (e.key === 'Escape') {
      onCloseSlash();
    }
  }, [onEnter, onDelete, onCloseSlash]);
  // Sync content from state if it changed externally (e.g., slash command clearing)
  useEffect(() => {
    if (ref.current && ref.current.textContent !== block.content) {
      ref.current.textContent = block.content;
    }
  }, [block.content]);
  if (block.type === 'divider') {
    return <div className="py-3 group" onClick={onFocus} data-id="element-150">
        <hr className="border-stone-200" data-id="element-151" />
      </div>;
  }
  const placeholderMap: Record<string, string> = {
    text: "Type '/' for commands...",
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    bulletList: 'List item',
    numberedList: 'List item',
    quote: 'Quote'
  };
  const styleMap: Record<string, string> = {
    text: 'text-base text-stone-800 leading-relaxed',
    heading1: 'text-3xl font-bold text-stone-900 leading-tight',
    heading2: 'text-2xl font-semibold text-stone-900 leading-tight',
    heading3: 'text-xl font-medium text-stone-900 leading-snug',
    bulletList: 'text-base text-stone-800 leading-relaxed',
    numberedList: 'text-base text-stone-800 leading-relaxed',
    quote: 'text-base italic text-stone-500 leading-relaxed'
  };
  const wrapperStyle = (): string => {
    if (block.type === 'quote') {
      return 'pl-4 border-l-[3px] border-stone-300';
    }
    if (block.type === 'bulletList') {
      return 'flex items-start gap-2';
    }
    if (block.type === 'numberedList') {
      return 'flex items-start gap-2';
    }
    return '';
  };
  const marker = (): React.ReactNode => {
    if (block.type === 'bulletList') {
      return <span className="text-stone-400 mt-[3px] select-none flex-shrink-0" aria-hidden="true" data-id="element-152">
          •
        </span>;
    }
    if (block.type === 'numberedList') {
      return <span className="text-stone-400 mt-[1px] text-sm tabular-nums select-none flex-shrink-0 min-w-[1.25rem]" aria-hidden="true" data-id="element-153">
          {index + 1}.
        </span>;
    }
    return null;
  };
  return <div className={`relative py-[2px] rounded-sm transition-colors duration-150 ${wrapperStyle()}`} data-id="element-154">
      {marker()}
      <div ref={ref} contentEditable suppressContentEditableWarning role="textbox" aria-label={`${block.type} block`} data-placeholder={placeholderMap[block.type] ?? "Type '/' for commands..."} className={`w-full outline-none min-h-[1.5em] ${styleMap[block.type] ?? styleMap.text}`} onInput={handleInput} onKeyDown={handleKeyDown} onFocus={onFocus} onCompositionStart={() => {
      isComposing.current = true;
    }} onCompositionEnd={() => {
      isComposing.current = false;
    }} data-id="element-155" />
    </div>;
}